import { ImageSquareIcon, TrashIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  ALLOWED_IMAGE_MIMES,
  MAX_IMAGES,
  formatBytes,
  validateImageFile,
} from '@/lib/issue-reporting/imageValidation';

/**
 * Controlled image picker for the "Report an issue" dialog. Supports click-to-choose, drag-and-drop,
 * and paste (the parent forwards pasted files via the imperative `addFiles` exposed through a ref-like
 * `onRequestAdd` is unnecessary — paste is handled in the parent and routed through `onChange`).
 *
 * Parent owns the `files` array; this component validates (MIME + 8 MiB + ≤10 cap) before anything
 * enters the list and renders object-URL thumbnails. The backend re-validates regardless.
 */
export interface ImageDropzoneProps {
  readonly files: readonly File[];
  readonly onChange: (next: File[]) => void;
  readonly disabled?: boolean;
}

export function ImageDropzone({ files, onChange, disabled = false }: ImageDropzoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<readonly string[]>([]);

  // Build + revoke object URLs in lock-step with the files array.
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
    };
  }, [files]);

  function addFiles(picked: FileList | readonly File[] | null): void {
    setError(null);
    if (picked === null) return;
    const incoming = Array.from(picked);
    if (incoming.length === 0) return;
    if (files.length + incoming.length > MAX_IMAGES) {
      setError(`You can attach at most ${String(MAX_IMAGES)} images.`);
      return;
    }
    for (const file of incoming) {
      const err = validateImageFile(file);
      if (err !== null) {
        setError(err);
        return;
      }
    }
    onChange([...files, ...incoming]);
  }

  function removeAt(index: number): void {
    setError(null);
    const next = files.slice();
    next.splice(index, 1);
    onChange(next);
  }

  function onPicked(e: ChangeEvent<HTMLInputElement>): void {
    addFiles(e.target.files);
    if (inputRef.current !== null) inputRef.current.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>): void {
    if (disabled) return;
    e.preventDefault();
  }

  return (
    <div className="space-y-2" data-testid="issue-image-dropzone">
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className={
          'flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-input p-4 text-center text-xs' +
          (disabled ? ' opacity-60' : '')
        }
      >
        <ImageSquareIcon size={22} weight="duotone" />
        <p className="text-muted-foreground">Drag images here, paste a screenshot, or</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          data-testid="issue-image-choose"
        >
          Choose images
        </Button>
        <p className="text-muted-foreground">PNG, JPEG, WEBP, or GIF up to 8 MB each (max {MAX_IMAGES}).</p>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_IMAGE_MIMES.join(',')}
          multiple
          className="hidden"
          onChange={onPicked}
          data-testid="issue-image-input"
        />
      </div>

      {error !== null && (
        <p className="text-xs text-destructive" data-testid="issue-image-error">
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="issue-image-list">
          {files.map((file, idx) => (
            <li
              key={`${file.name}-${String(file.size)}-${String(idx)}`}
              className="group relative overflow-hidden rounded-md border border-input"
              data-testid={`issue-image-row-${String(idx)}`}
            >
              {previews[idx] !== undefined && (
                <img
                  src={previews[idx]}
                  alt={file.name}
                  className="aspect-square w-full object-cover"
                />
              )}
              <div className="truncate bg-muted/40 px-1.5 py-1 text-[10px] text-muted-foreground">
                {formatBytes(file.size)}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={() => { removeAt(idx); }}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                data-testid={`issue-image-remove-${String(idx)}`}
                className="absolute right-1 top-1 h-6 w-6 opacity-90"
              >
                <TrashIcon size={12} weight="bold" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
